import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
   Search, ChevronLeft, ChevronRight, Eye, 
  ShieldAlert, Globe, X, Terminal 
} from 'lucide-react';

// --- Color Logic for Actions ---
const getActionColor = (action) => {
  if (action.includes('DELETE') || action.includes('DESTROY') || action.includes('REVOKE')) return 'bg-red-100 text-red-700 border-red-200';
  if (action.includes('CREATE') || action.includes('GENERATE') || action.includes('ADD')) return 'bg-green-100 text-green-700 border-green-200';
  if (action.includes('UPDATE') || action.includes('EDIT')) return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const AuditLogsPage = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null); // For Modal

  // Fetch Logs
  const { data, isLoading } = useQuery({ 
    queryKey: ['audit_logs', search, page], 
    queryFn: () => axiosClient.get('/admin/audit-logs', { params: { search, page, limit: 15 } }),
    keepPreviousData: true
  });

  const { logs = [], total = 0 } = data || {};
  const totalPages = Math.ceil(total / 15);

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center">
            <ShieldAlert className="w-6 h-6 mr-3 text-slate-600" />
            System Audit Logs
          </h1>
          <p className="text-sm text-slate-500">Forensic trail of all administrative actions. Total {total} records.</p>
        </div>
        
        <div className="relative w-full md:w-64">
          <input 
            className="pl-9 pr-4 py-2 border rounded-lg text-sm w-full focus:ring-2 focus:ring-indigo-500 outline-none"
            placeholder="Search Action, IP, User..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Timestamp</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Actor</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Action</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Target</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">IP Address</th>
              <th className="px-6 py-4 text-right">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
               <tr><td colSpan="6" className="p-8 text-center text-slate-400">Loading audit trail...</td></tr>
            ) : logs.length === 0 ? (
               <tr><td colSpan="6" className="p-8 text-center text-slate-400">No logs found matching query.</td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/50 transition-colors font-mono text-sm">
                  <td className="px-6 py-4 text-slate-500">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                       <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 mr-2">
                          {log.actor_name ? log.actor_name[0] : '?'}
                       </div>
                       <div>
                          <p className="text-slate-800 font-semibold">{log.actor_name || 'System'}</p>
                          <p className="text-[10px] text-slate-400">{log.actor_email}</p>
                       </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold border ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {log.entity_type} <span className="text-slate-400">#{log.entity_id}</span>
                  </td>
                  <td className="px-6 py-4 text-slate-500 flex items-center">
                    <Globe className="w-3 h-3 mr-1 text-slate-400" /> {log.ip_address}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button 
                      onClick={() => setSelectedLog(log)}
                      className="p-1.5 hover:bg-indigo-50 text-indigo-600 rounded transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-2 border rounded hover:bg-white disabled:opacity-50"><ChevronLeft className="w-4 h-4"/></button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="p-2 border rounded hover:bg-white disabled:opacity-50"><ChevronRight className="w-4 h-4"/></button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 backdrop-blur-sm p-4">
           <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                 <h3 className="font-bold text-slate-800 flex items-center">
                    <Terminal className="w-5 h-5 mr-2 text-slate-500"/> Audit Record #{selectedLog.id}
                 </h3>
                 <button onClick={() => setSelectedLog(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600"/></button>
              </div>
              
              <div className="p-6 space-y-4">
                 <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-xs text-slate-400 uppercase font-bold">Actor</p>
                        <p className="text-slate-800">{selectedLog.actor_name} ({selectedLog.actor_role})</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase font-bold">IP Source</p>
                        <p className="text-slate-800">{selectedLog.ip_address}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase font-bold">Action</p>
                        <p className="text-indigo-600 font-bold">{selectedLog.action}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase font-bold">Time</p>
                        <p className="text-slate-800">{new Date(selectedLog.created_at).toLocaleString()}</p>
                    </div>
                 </div>

                 <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                    <p className="text-xs text-slate-400 uppercase font-bold mb-2">Change Payload (JSON)</p>
                    <pre className="text-xs text-green-400 font-mono">
                        {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                 </div>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default AuditLogsPage;